import { getDocument, GlobalWorkerOptions } from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.min.js?url";

// Set up the worker for PDF.js
GlobalWorkerOptions.workerSrc = pdfjsWorker;

/**
 * Generates a thumbnail from the first page of a PDF file
 * @param {File} pdfFile - The PDF file to generate thumbnail from
 * @param {Object} options - Options for thumbnail generation
 * @param {number} options.width - Width of the thumbnail (default: 200)
 * @param {number} options.height - Height of the thumbnail (default: 200)
 * @param {number} options.quality - Image quality 0-1 (default: 0.8)
 * @returns {Promise<string>} - Data URL of the thumbnail image
 */
export const generatePdfThumbnail = async (pdfFile, options = {}) => {
  const { width = 200, height = 200, quality = 0.8 } = options;

  try {
    // Create object URL for the PDF file
    const pdfUrl = URL.createObjectURL(pdfFile);

    // Load the PDF document
    const loadingTask = getDocument(pdfUrl);
    const pdfDocument = await loadingTask.promise;

    // Get the first page
    const page = await pdfDocument.getPage(1);

    // Calculate scale to fit within the desired dimensions
    const viewport = page.getViewport({ scale: 1 });
    const scale = Math.min(width / viewport.width, height / viewport.height);
    const scaledViewport = page.getViewport({ scale });

    // Create canvas
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    canvas.width = width;
    canvas.height = height;

    // Set white background
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);

    // Render the PDF page to canvas
    const renderContext = {
      canvasContext: context,
      viewport: scaledViewport,
    };

    await page.render(renderContext).promise;

    // Convert canvas to data URL
    const dataUrl = canvas.toDataURL("image/jpeg", quality);

    // Clean up
    URL.revokeObjectURL(pdfUrl);

    return dataUrl;
  } catch (error) {
    console.error("Error generating PDF thumbnail:", error);
    throw new Error("Failed to generate PDF thumbnail");
  }
};

/**
 * Generates thumbnails for multiple PDF files
 * @param {File[]} pdfFiles - Array of PDF files
 * @param {Object} options - Options for thumbnail generation
 * @returns {Promise<Array>} - Array of objects with file and thumbnail data URL
 */
export const generatePdfThumbnails = async (pdfFiles, options = {}) => {
  const results = [];

  for (const file of pdfFiles) {
    try {
      const thumbnail = await generatePdfThumbnail(file, options);
      results.push({
        file,
        thumbnail,
        error: null,
      });
    } catch (error) {
      results.push({
        file,
        thumbnail: null,
        error: error.message,
      });
    }
  }

  return results;
};
